import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, Home, Heart } from 'lucide-react';
import { toast } from 'sonner';
import { performSearchLocal, SearchResult } from '@/lib/osint-api';
import ResultCard from '@/components/ResultCard';

export default function Dashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('search');
  const [searchType, setSearchType] = useState<'email' | 'username' | 'mobile'>('email');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) {
      toast.error('Please enter a search query');
      return;
    }

    setLoading(true);
    try {
      const response = await performSearchLocal(searchType, query);
      setResults(response.results);
      setQuery('');
      toast.success(`Found ${response.count} result(s)`);
    } catch (error) {
      toast.error('Search failed. Please try again.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFavorite = (resultId: string) => {
    if (favorites.has(resultId)) {
      setFavorites((prev) => {
        const newSet = new Set(prev);
        newSet.delete(resultId);
        return newSet;
      });
      toast.success('Removed from favorites');
    } else {
      setFavorites((prev) => new Set(prev).add(resultId));
      toast.success('Added to favorites');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
      {/* Header */}
      <header className="bg-white dark:bg-slate-800 border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold">OSINT Platform</h1>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/')}
            className="gap-2"
          >
            <Home className="w-4 h-4" />
            Home
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8">
            <TabsTrigger value="search" className="gap-2">
              <Search className="w-4 h-4" />
              Search
            </TabsTrigger>
            <TabsTrigger value="favorites" className="gap-2">
              <Heart className="w-4 h-4" />
              Favorites ({favorites.size})
            </TabsTrigger>
          </TabsList>

          {/* Search Tab */}
          <TabsContent value="search">
            <Card className="mb-8">
              <div className="p-8">
                <h2 className="text-xl font-semibold mb-6">Search Profiles</h2>

                <Tabs value={searchType} onValueChange={(v: any) => setSearchType(v)}>
                  <TabsList className="grid w-full max-w-md grid-cols-3">
                    <TabsTrigger value="email">Email</TabsTrigger>
                    <TabsTrigger value="username">Username</TabsTrigger>
                    <TabsTrigger value="mobile">Mobile</TabsTrigger>
                  </TabsList>

                  {(['email', 'username', 'mobile'] as const).map((type) => (
                    <TabsContent key={type} value={type}>
                      <form onSubmit={handleSearch} className="mt-6 flex gap-2">
                        <Input
                          placeholder={
                            type === 'email'
                              ? 'Enter email address'
                              : type === 'username'
                              ? 'Enter username'
                              : 'Enter phone number'
                          }
                          value={query}
                          onChange={(e) => setQuery(e.target.value)}
                          className="flex-1"
                        />
                        <Button type="submit" disabled={loading} className="gap-2">
                          <Search className="w-4 h-4" />
                          {loading ? 'Searching...' : 'Search'}
                        </Button>
                      </form>
                    </TabsContent>
                  ))}
                </Tabs>
              </div>
            </Card>

            {/* Results */}
            {results.length === 0 ? (
              <Card className="text-center py-12">
                <p className="text-muted-foreground">
                  Start searching by entering an email, username, or mobile number
                </p>
              </Card>
            ) : (
              <div>
                <h3 className="text-lg font-semibold mb-4">Results ({results.length})</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {results.map((result) => (
                    <ResultCard
                      key={result.id}
                      result={result}
                      isFavorited={favorites.has(result.id)}
                      onFavorite={handleFavorite}
                    />
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Favorites Tab */}
          <TabsContent value="favorites">
            {favorites.size === 0 ? (
              <Card className="text-center py-12">
                <Heart className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">
                  No favorites yet. Search and add results to your favorites.
                </p>
              </Card>
            ) : (
              <div>
                <h3 className="text-lg font-semibold mb-4">Saved Favorites ({favorites.size})</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {results.filter((r) => favorites.has(r.id)).map((result) => (
                    <ResultCard
                      key={result.id}
                      result={result}
                      isFavorited={true}
                      onFavorite={handleFavorite}
                    />
                  ))}
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}